import { joinUniqId } from '@futrli/shared-services';

import { REPORT_SECTION, PeriodType, PERIOD_TYPE_OPTIONS } from '../../../../core/constants';
import { ROW_KIND } from '../../../../forecast/detail/reports/data-grid/constants';
import {
  NumberExplainedMessagesMap,
  NumberExplainedProps,
  NumberExplainedReportMessageSchema,
  NumberExplainedResponse,
  MessageEntryResponse
} from './types';

export const NOT_ALLOWED_NUMBER_EXPLAINED: NumberExplainedProps = {
  allowed: false,
  data: null
};

export enum NumberExplainedMessagesIds {
  ComingSoon = 'predict.number_explained.coming_soon',
  CurrentlyUnused = 'predict.number_explained.currently_unused',
  ErrorMessage = 'predict.number_explained.error',
  ThisMonthMessage = 'predict.number_explained.this_month_explained',
  ThisWeekMessage = 'predict.number_explained.this_week_explained',
  PredictedActivity = 'predict.number_explained.predicted_activity',
  PredictedCashImpact = 'predict.number_explained.predicted_cash_impact',
  Monthly_Message1 = 'predict.number_explained.month.message1',
  Monthly_Message2 = 'predict.number_explained.month.message2',
  Monthly_Message3 = 'predict.number_explained.month.message3',
  Monthly_Message4 = 'predict.number_explained.month.message4',
  Monthly_Message5 = 'predict.number_explained.month.message5',
  Monthly_Message6 = 'predict.number_explained.month.message6',
  Monthly_Message7 = 'predict.number_explained.month.message7',
  Weekly_Message1 = 'predict.number_explained.week.message1',
  Weekly_Message2 = 'predict.number_explained.week.message2',
  Weekly_Message3 = 'predict.number_explained.week.message3',
  Weekly_Message4 = 'predict.number_explained.week.message4',
  Weekly_Message5 = 'predict.number_explained.week.message5',
  Weekly_Message6 = 'predict.number_explained.week.message6',
  Weekly_Message7 = 'predict.number_explained.week.message7'
}

const unifiedContent = `
  <p>This figure is the predicted combined month-end amount based on yesterday's balance.</p>
  <p>{br}</p>
  <p>Your total {accountCategory} for the month, for all {accountCategory} accounts up until yesterday is <strong>{actual}</strong>.</p>
  <p>{br}</p>
  <p>The Predicted total from today until the end of the month is <strong>{predicted}</strong>.</p>
  <p>{br}</p>
  <p>All values are excluding {taxName}.</p>`;
export const numberExplainedMessagesMap: NumberExplainedMessagesMap = {
  [NumberExplainedMessagesIds.ComingSoon]: 'Explanation coming soon!',
  [NumberExplainedMessagesIds.CurrentlyUnused]: 'Currently unused!',
  [NumberExplainedMessagesIds.ErrorMessage]: 'The explanation of these numbers is temporarily unavailable',
  [NumberExplainedMessagesIds.ThisMonthMessage]: 'THIS MONTH EXPLAINED',
  [NumberExplainedMessagesIds.ThisWeekMessage]: 'THIS WEEK EXPLAINED',
  [NumberExplainedMessagesIds.PredictedActivity]: 'THIS NUMBER EXPLAINED - PREDICTED ACTIVITY',
  [NumberExplainedMessagesIds.PredictedCashImpact]: 'THIS NUMBER EXPLAINED - PREDICTED CASH IMPACT',
  [NumberExplainedMessagesIds.Monthly_Message1]: unifiedContent,
  [NumberExplainedMessagesIds.Monthly_Message2]: unifiedContent,
  [NumberExplainedMessagesIds.Monthly_Message3]: `
    <p>This is the total predicted cash coming in/out <strong>from today</strong> until the end of this month for <strong>all</strong> these account codes.</p>
    <p>(Note: All cash movements up until yesterday are already showing in your predicted cash balance.)</p>`,
  [NumberExplainedMessagesIds.Monthly_Message4]: unifiedContent,
  [NumberExplainedMessagesIds.Monthly_Message5]: `
    <p>This is the total predicted cash coming in/out from for this account <strong>from today</strong> until the end of this month.</p>
    <p>(Note: All cash movements up until yesterday are already showing in your predicted cash balance.)</p>`,
  [NumberExplainedMessagesIds.Monthly_Message6]: unifiedContent,
  [NumberExplainedMessagesIds.Monthly_Message7]: unifiedContent,
  [NumberExplainedMessagesIds.Weekly_Message1]: `
    <p>In <strong>Cash impact</strong> view we show the <strong>{accountCategory}</strong> you’ll have at the end of this week based on yesterday’s actual balance. In <strong>Activity</strong> view there are 3 numbers:</p>
    <p>On the left is the combined cash balance of <strong>all</strong> of your bank accounts for the week <strong>up until yesterday</strong>, which includes all cash movements that have happened.</p>
    <p>On the right are 2 numbers:
      {br}The bottom figure is the predicted cash movements <strong>from today</strong> until the end of this week (this is hidden when rows are displayed in small view).
      {br}The top figure is the <strong>total predicted cash</strong> you’ll have at the end of this week based on yesterday’s actual balance.
    </p>`,
  [NumberExplainedMessagesIds.Weekly_Message2]: `
    <p>On the left is your total {accountCategory} for the week for <strong>all</strong> {accountCategory} accounts <strong>up until yesterday</strong>.</p>
    <p>On the right are 2 numbers:
      {br}The bottom figure is the predicted total <strong>from today</strong> until the end of this week (this is hidden when rows are displayed in small view).
      {br}The top figure is the <strong>predicted combined weekly total</strong> based on yesterday’s balance.
    </p>
    <p>All values are ex. VAT/ GST.</p>`,
  [NumberExplainedMessagesIds.Weekly_Message3]: `
    <p>This is the total predicted cash coming in/out <strong>from today</strong> until the end of this week for <strong>all</strong> these account codes.</p>
    <p>(Note: All cash movements up until yesterday are already showing in your predicted cash balance.)</p>`,
  [NumberExplainedMessagesIds.Weekly_Message4]: `
    <p>On the left is your {accountCategory} for the week for this account <strong>up until yesterday</strong>.</p>
    <p>On the right are 2 numbers:
      {br}The bottom figure is the predicted {accountCategory} <strong>from today</strong> until the end of this week (this is hidden when rows are displayed in small view).
      {br}The top figure is the <strong>predicted combined weekly total</strong> based on yesterday’s balance.
    </p>
    <p>All values are ex. VAT/ GST.</p>`,
  [NumberExplainedMessagesIds.Weekly_Message5]: `
    <p>This is the total predicted cash coming in/out from for this account <strong>from today</strong> until the end of this week.</p>
    <p>(Note: All cash movements up until yesterday are already showing in your predicted cash balance.)</p>`,
  [NumberExplainedMessagesIds.Weekly_Message6]: `
    <p>On the left is your actual balance for the week <strong>up until yesterday</strong>.</p>
    <p>On the right are 2 numbers:
      {br}The bottom figure is the predicted total movement <strong>from today</strong> until the end of this week (this is hidden when rows are displayed in small view).
      {br}The top figure is the predicted end of week balance, based on your actual balance from yesterday.
    </p>`,
  [NumberExplainedMessagesIds.Weekly_Message7]: `
    <p>On the left is your actual balance on this account for the week <strong>up until yesterday</strong>.</p>
    <p>On the right are 2 numbers:
      {br}The bottom figure is the predicted total movement <strong>from today</strong> until the end of this week (this is hidden when rows are displayed in small view).
      {br}The top figure is the predicted end of week balance, based on your actual balance from yesterday.
    </p>`
};

export const numberExplainedReportMessageSchema: NumberExplainedReportMessageSchema = {
  'balance_sheet::bank_accounts::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message1,
    { accountCategory: 'total predicted cash' }
  ],

  'profit_loss::income::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message2,
    { accountCategory: 'sales' }
  ],
  'profit_loss::other_income::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message2,
    { accountCategory: 'other income' }
  ],
  'profit_loss::expenses::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message2,
    { accountCategory: 'expenses' }
  ],
  'profit_loss::other_expenses::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message2,
    { accountCategory: 'other expenses' }
  ],
  'profit_loss::cost_of_sales::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message2,
    { accountCategory: 'cost of sales' }
  ],

  'cash_flow::income::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message3,
    null
  ],
  'cash_flow::other_income::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message3,
    null
  ],
  'cash_flow::expenses::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message3,
    null
  ],
  'cash_flow::other_expenses::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message3,
    null
  ],
  'cash_flow::cost_of_sales::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message3,
    null
  ],
  'cash_flow::current_assets::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message3,
    null
  ],
  'cash_flow::fixed_assets::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message3,
    null
  ],
  'cash_flow::non_current_assets::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message3,
    null
  ],
  'cash_flow::current_liabilities::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message3,
    null
  ],
  'cash_flow::non_current_liabilities::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message3,
    null
  ],
  'cash_flow::equity::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message3,
    null
  ],
  'cash_flow::cash_from_business_activities::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message3,
    null
  ],
  'cash_flow::cash_from_financing_and_investing::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message3,
    null
  ],

  'profit_loss::income::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message4,
    { accountCategory: 'sales' }
  ],
  'profit_loss::other_income::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message4,
    { accountCategory: 'other income' }
  ],
  'profit_loss::expenses::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message4,
    { accountCategory: 'expenses' }
  ],
  'profit_loss::other_expenses::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message4,
    { accountCategory: 'other expenses' }
  ],
  'profit_loss::cost_of_sales::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message4,
    { accountCategory: 'cost of sales' }
  ],

  'cash_flow::income::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message5,
    null
  ],
  'cash_flow::other_income::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message5,
    null
  ],
  'cash_flow::expenses::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message5,
    null
  ],
  'cash_flow::other_expenses::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message5,
    null
  ],
  'cash_flow::cost_of_sales::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message5,
    null
  ],
  'cash_flow::current_assets::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message5,
    null
  ],
  'cash_flow::fixed_assets::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message5,
    null
  ],
  'cash_flow::non_current_assets::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message5,
    null
  ],
  'cash_flow::current_liabilities::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message5,
    null
  ],
  'cash_flow::non_current_liabilities::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message5,
    null
  ],
  'cash_flow::equity::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message5,
    null
  ],

  'balance_sheet::bank_accounts::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message6,
    null
  ],
  'balance_sheet::current_assets::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message6,
    null
  ],
  'balance_sheet::fixed_assets::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message6,
    null
  ],
  'balance_sheet::non_current_assets::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message6,
    null
  ],
  'balance_sheet::current_liabilities::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message6,
    null
  ],
  'balance_sheet::non_current_liabilities::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message6,
    null
  ],
  'balance_sheet::equity::account::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message6,
    null
  ],

  'balance_sheet::current_assets::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message7,
    null
  ],
  'balance_sheet::fixed_assets::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message7,
    null
  ],
  'balance_sheet::non_current_assets::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message7,
    null
  ],
  'balance_sheet::current_liabilities::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message7,
    null
  ],
  'balance_sheet::non_current_liabilities::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message7,
    null
  ],
  'balance_sheet::equity::total::monthly': [
    NumberExplainedMessagesIds.ThisMonthMessage,
    NumberExplainedMessagesIds.Monthly_Message7,
    null
  ],

  'balance_sheet::bank_accounts::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message1,
    { accountCategory: 'total predicted cash' }
  ],

  'profit_loss::income::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message2,
    { accountCategory: 'sales' }
  ],
  'profit_loss::other_income::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message2,
    { accountCategory: 'other income' }
  ],
  'profit_loss::expenses::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message2,
    { accountCategory: 'expenses' }
  ],
  'profit_loss::other_expenses::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message2,
    { accountCategory: 'other expenses' }
  ],
  'profit_loss::cost_of_sales::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message2,
    { accountCategory: 'cost of sales' }
  ],

  'cash_flow::income::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message3,
    null
  ],
  'cash_flow::other_income::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message3,
    null
  ],
  'cash_flow::expenses::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message3,
    null
  ],
  'cash_flow::other_expenses::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message3,
    null
  ],
  'cash_flow::cost_of_sales::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message3,
    null
  ],
  'cash_flow::current_assets::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message3,
    null
  ],
  'cash_flow::fixed_assets::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message3,
    null
  ],
  'cash_flow::non_current_assets::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message3,
    null
  ],
  'cash_flow::current_liabilities::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message3,
    null
  ],
  'cash_flow::non_current_liabilities::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message3,
    null
  ],
  'cash_flow::equity::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message3,
    null
  ],
  'cash_flow::cash_from_business_activities::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message3,
    null
  ],
  'cash_flow::cash_from_financing_and_investing::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message3,
    null
  ],

  'profit_loss::income::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message4,
    { accountCategory: 'sales' }
  ],
  'profit_loss::other_income::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message4,
    { accountCategory: 'other income' }
  ],
  'profit_loss::expenses::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message4,
    { accountCategory: 'expenses' }
  ],
  'profit_loss::other_expenses::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message4,
    { accountCategory: 'other expenses' }
  ],
  'profit_loss::cost_of_sales::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message4,
    { accountCategory: 'cost of sales' }
  ],

  'cash_flow::income::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message5,
    null
  ],
  'cash_flow::other_income::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message5,
    null
  ],
  'cash_flow::expenses::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message5,
    null
  ],
  'cash_flow::other_expenses::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message5,
    null
  ],
  'cash_flow::cost_of_sales::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message5,
    null
  ],
  'cash_flow::current_assets::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message5,
    null
  ],
  'cash_flow::fixed_assets::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message5,
    null
  ],
  'cash_flow::non_current_assets::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message5,
    null
  ],
  'cash_flow::current_liabilities::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message5,
    null
  ],
  'cash_flow::non_current_liabilities::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message5,
    null
  ],
  'cash_flow::equity::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message5,
    null
  ],

  'balance_sheet::bank_accounts::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message6,
    null
  ],
  'balance_sheet::current_assets::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message6,
    null
  ],
  'balance_sheet::fixed_assets::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message6,
    null
  ],
  'balance_sheet::non_current_assets::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message6,
    null
  ],
  'balance_sheet::current_liabilities::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message6,
    null
  ],
  'balance_sheet::non_current_liabilities::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message6,
    null
  ],
  'balance_sheet::equity::account::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message6,
    null
  ],

  'balance_sheet::current_assets::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message7,
    null
  ],
  'balance_sheet::fixed_assets::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message7,
    null
  ],
  'balance_sheet::non_current_assets::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message7,
    null
  ],
  'balance_sheet::current_liabilities::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message7,
    null
  ],
  'balance_sheet::non_current_liabilities::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message7,
    null
  ],
  'balance_sheet::equity::total::weekly': [
    NumberExplainedMessagesIds.ThisWeekMessage,
    NumberExplainedMessagesIds.Weekly_Message7,
    null
  ]
};

export const getMessageEntryFormat = (messageId: NumberExplainedMessagesIds | null): MessageEntryResponse => {
  const message = messageId && numberExplainedMessagesMap[messageId];
  return message && messageId ? [messageId, message] : [];
};

export const generateNumberExplainedLocalResponse = (
  reportSection: REPORT_SECTION,
  accountId: string,
  type: ROW_KIND,
  periodType: PeriodType
): NumberExplainedResponse => {
  const periodKind = periodType === PERIOD_TYPE_OPTIONS.MONTHLY ? 'monthly' : 'weekly';
  const id = joinUniqId(reportSection, accountId, type, periodKind);

  const [titleId, contentId, context] = numberExplainedReportMessageSchema[id] ?? [
    null,
    NumberExplainedMessagesIds.ComingSoon,
    null
  ];

  return {
    titleId,
    contentId,
    context,
    explanation: null,
    activity_explanation: null,
    activity: null,
    cash: null,
    collection_days: null,
    transactions_vat: null,
    type: 'empty'
  };
};
